import { Subscription } from 'rxjs';

import {
  AfterContentInit, Component, ContentChildren, forwardRef, HostBinding,
  HostListener, input, OnDestroy, output, QueryList
} from '@angular/core';

import { AccordionLayoutEnum } from '../../_enums/accordion-layout.enum';
import { SdAccordionComponent } from './accordion/accordion.component';
import { SdThemeComponent } from '../abstract/theme/theme.component';
import { PositionEnum } from '../../_enums/position.enum';

@Component({
  selector: 'sd-accordion-group',
  templateUrl: './accordion-group.component.html',
  standalone: true
})
export class SdAccordionGroupComponent extends SdThemeComponent implements AfterContentInit, OnDestroy {
  /**
   * While opening an accordion, defined if the other accordion stay open (or not)
   */
  closeOthers = input<boolean>();
  /**
   * Defined if you can close a group while clicking outside
   */
  clickOutsideToClose = input(false);
  /**
   * While opening an accordion, defined if the other accordion stay visible (or not)
   */
  hideOthers = input(false);
  layout = input<AccordionLayoutEnum>();
  class = input('');

  /**
   * Emitted while an accordion is open
   */
  isOpened = output<boolean>();
  /**
   * Emitted while a click outside is performed
   */
  clickOut = output<boolean>();

  @ContentChildren(SdAccordionComponent)
  accordions: QueryList<SdAccordionComponent>;

  @ContentChildren(forwardRef(() => SdAccordionGroupComponent))
  accordionGroups: QueryList<SdAccordionGroupComponent>;

  @HostBinding('attr.role')
  role = 'presentation';

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class(), this.layout() ? 'is-' + this.layout() : null].filter(Boolean).join(' ');
  }

  private subscribeGroup = new Subscription();
  positionEnum = PositionEnum;

  ngAfterContentInit() {
    this.accordions.forEach((accordion) =>
      this.subscribeGroup.add(
        accordion.toggled.subscribe(() => {
          this.openGroup(accordion);
        }),
      ),
    );

    this.accordions.changes.subscribe((accordions) => {
      const oneOpened = accordions.some((accordion) => accordion.isOpened());

      if (oneOpened && this.hideOthers()) {
        accordions.forEach((accordion) => {
          if (accordion.isVisible() && !accordion.isOpened()) {
            accordion.isVisible.set(false);
          }
        });
      }

      this.subscribeGroup?.unsubscribe();
      this.subscribeGroup = new Subscription();

      accordions.forEach((accordion) =>
        this.subscribeGroup.add(
          accordion.toggled.subscribe(() => {
            this.openGroup(accordion);
          }),
        ),
      );
    });
  }

  ngOnDestroy(): void {
    this.subscribeGroup?.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onClickOut(event) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      if (this.clickOutsideToClose() || this.closeOthers()) {
        this.closeGroup();
        if (this.hideOthers()) {
          this.showGroup();
        }
        this.isOpened.emit(false);
      }
    }
  }

  closeGroup() {
    this.accordionGroups.forEach((group: SdAccordionGroupComponent) => {
      group.accordions.forEach((accordion: SdAccordionComponent) => {
        accordion.isOpened.set(false);
      });
    });
    this.accordions.forEach((accordion: SdAccordionComponent) => {
      accordion.isOpened.set(false);
    });
  }

  hideGroup() {
    this.accordionGroups.forEach((group: SdAccordionGroupComponent) => {
      group.accordions.forEach((accordion: SdAccordionComponent) => {
        accordion.isVisible.set(false);
      });
    });
    this.accordions.forEach((accordion: SdAccordionComponent) => {
      accordion.isVisible.set(false);
    });
  }

  showGroup() {
    this.accordionGroups.forEach((group: SdAccordionGroupComponent) => {
      group.accordions.forEach((accordion: SdAccordionComponent) => {
        accordion.isVisible.set(true);
      });
    });
    this.accordions.forEach((accordion: SdAccordionComponent) => {
      accordion.isVisible.set(true);
    });
  }

  /**
   * Open an accordion group
   * @param currentAccordion SdAccordionComponent instance
   */
  openGroup(currentAccordion?: SdAccordionComponent) {
    if (currentAccordion.isOpened() === false && this.closeOthers()) {
      this.closeGroup();
    }

    if (currentAccordion.isOpened() === false && this.hideOthers()) {
      this.closeGroup();
      this.hideGroup();
      currentAccordion.isVisible.set(true);
    } else if (currentAccordion.isOpened() === true && this.hideOthers()) {
      this.showGroup();
    }

    currentAccordion.isOpened.set(!currentAccordion.isOpened());
    this.isOpened.emit(currentAccordion.isOpened());
  }
}
